/**
 * What the Mail page can work out without asking anyone.
 *
 * The page is an onboarding flow in three steps, and the state of each step
 * is a function of data that already exists: the domains and whether Woyra
 * has verified them, the wallet against the price, the mailboxes. Deriving
 * that here rather than inside the component keeps the rules testable and
 * keeps the page from holding a second copy of the truth that can drift from
 * the server's.
 */

import type { Tone } from '../components/ui'
import type {
  Mailbox,
  MailDomain,
  MailDomainStatus,
  MailPricing,
  MailPlan,
  Wallet,
} from './types'

/* -------------------------------------------------------------------------
   Money
   ------------------------------------------------------------------------- */

const ETB = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'ETB',
  currencyDisplay: 'code',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

/** "ETB 349.00". The code rather than a symbol, because the Birr has no
 *  symbol a customer would recognise and "Br" reads as a typo. The
 *  formatter's non-breaking space is replaced so the string is the same
 *  wherever it is compared. */
export function etb(amount: string | number | null | undefined): string {
  const value = typeof amount === 'string' ? Number.parseFloat(amount) : amount
  if (value == null || !Number.isFinite(value)) return '—'
  return ETB.format(value).replace(/\u00a0/g, ' ')
}

/** The Birr balance, whichever wallet the account bills its plans from.
 *  Mailboxes are priced in Birr and charged from the Birr wallet, so a
 *  customer who pays for applications in dollars still needs Birr here. */
export function etbBalance(
  wallet: Pick<Wallet, 'balances' | 'balance' | 'currency'> | null | undefined,
): number {
  if (!wallet) return 0
  const raw = wallet.balances?.ETB ?? (wallet.currency === 'ETB' ? wallet.balance : '0')
  const value = Number.parseFloat(raw)
  return Number.isFinite(value) ? value : 0
}

export function monthlyPrice(
  pricing: (Pick<MailPricing, 'mailbox_monthly_etb'> & Partial<Pick<MailPricing, 'plans'>>) | null | undefined,
  plan: MailPlan = 'standard',
): number | null {
  if (!pricing) return null
  const raw = pricing.plans?.find(item => item.tier === plan)?.monthly_etb
    ?? (plan === 'standard' ? pricing.mailbox_monthly_etb : undefined)
  if (raw == null) return null
  const value = Number.parseFloat(raw)
  return Number.isFinite(value) ? value : null
}

/** How much is missing to cover `due`, never negative, in whole cents. */
export function shortfall(balance: number, due: number): number {
  return Math.max(0, Math.round((due - balance) * 100) / 100)
}

export function monthsCovered(balance: number, due: number): number {
  if (!(due > 0)) return 0
  return Math.max(0, Math.floor(balance / due))
}

/** Billing's top-up form, opened on the Birr wallet with the amount filled
 *  in and a way back here once the money has landed. Billing clamps the
 *  amount to its own minimum, so a small shortfall still lands on a valid
 *  form. */
export function topUpPath(amount: number | string): string {
  const value = typeof amount === 'string' ? Number.parseFloat(amount) : amount
  const params = new URLSearchParams({ add: '1', currency: 'ETB' })
  if (Number.isFinite(value) && value > 0) params.set('amount', value.toFixed(2))
  params.set('returnTo', '/mail')
  return `/billing?${params}`
}

/* -------------------------------------------------------------------------
   Addresses
   ------------------------------------------------------------------------- */

/** The server's rule, repeated so the form can refuse before a round trip:
 *  letters, digits, dots, dashes and underscores, starting and ending with a
 *  letter or digit, at most 64 characters. */
const LOCAL_PART = /^[a-z0-9]([a-z0-9._-]{0,62}[a-z0-9])?$/

export function isLocalPart(value: string): boolean {
  return LOCAL_PART.test(value.trim().toLowerCase())
}

/** Addresses typed into one box, separated however the person separated
 *  them. Lowercased and deduplicated because a mail address is not case
 *  sensitive in practice and a destination listed twice delivers twice. */
export function splitAddresses(raw: string): string[] {
  const seen = new Set<string>()
  for (const part of raw.split(/[\s,;]+/)) {
    const address = part.trim().toLowerCase()
    if (address) seen.add(address)
  }
  return [...seen]
}

/* -------------------------------------------------------------------------
   Status labels
   ------------------------------------------------------------------------- */

/** What a DNS record is for, in the words a registrar's help page uses. The
 *  server sends the kind in lower case; "Spf" is not a word anybody has read. */
const RECORD_KIND: Record<string, string> = {
  ownership: 'Ownership',
  spf: 'SPF',
  dkim: 'DKIM',
  dmarc: 'DMARC',
  mx: 'Incoming mail',
}

export function recordKindLabel(kind: string): string {
  return RECORD_KIND[kind.toLowerCase()] ?? kind
}

export function isVerifiedDomain(domain: Pick<MailDomain, 'status'>): boolean {
  return domain.status === 'verified' || domain.status === 'active'
}

export function domainStatusLabel(status: MailDomainStatus): { label: string; tone: Tone } {
  switch (status) {
    case 'active':
      return { label: 'Active', tone: 'ok' }
    case 'verified':
      return { label: 'Verified', tone: 'ok' }
    case 'pending':
      return { label: 'Pending DNS', tone: 'warn' }
    case 'failed':
      return { label: 'Failed', tone: 'bad' }
    case 'deleted':
      return { label: 'Removed', tone: 'idle' }
  }
}

/** "Paused: unpaid" and "Paused" are different states with different fixes:
 *  one is resolved at Billing, the other by the person who paused it. The
 *  pill has to say which. */
export function mailboxStatusLabel(
  box: Pick<Mailbox, 'status' | 'suspended_for_billing'>,
): { label: string; tone: Tone } {
  switch (box.status) {
    case 'active':
      return { label: 'Active', tone: 'ok' }
    case 'creating':
      return { label: 'Creating', tone: 'busy' }
    case 'suspended':
      return box.suspended_for_billing
        ? { label: 'Paused: unpaid', tone: 'warn' }
        : { label: 'Paused', tone: 'idle' }
    case 'failed':
      return { label: 'Failed', tone: 'bad' }
    case 'deleted':
      return { label: 'Deleted', tone: 'idle' }
  }
}

/* -------------------------------------------------------------------------
   The three steps
   ------------------------------------------------------------------------- */

export type StepState = 'todo' | 'current' | 'done' | 'attention'

export interface SetupStep {
  key: 'domain' | 'payment' | 'mailboxes'
  title: string
  state: StepState
  /** One line under the title, in the customer's terms. */
  summary: string
}

export interface SetupInput {
  domains: MailDomain[]
  mailboxes: Mailbox[]
  wallet: Pick<Wallet, 'balances' | 'balance' | 'currency'> | null
  pricing: Pick<MailPricing, 'mailbox_monthly_etb'> | null
}

function plural(count: number, one: string, many = `${one}es`): string {
  return `${count} ${count === 1 ? one : many}`
}

export function deriveSteps({ domains, mailboxes, wallet, pricing }: SetupInput): SetupStep[] {
  const live = domains.filter(domain => domain.status !== 'deleted')
  const verified = live.filter(isVerifiedDomain)
  const boxes = mailboxes.filter(box => box.status !== 'deleted')
  const unpaid = boxes.filter(box => box.suspended_for_billing)
  // What the next renewal round takes: every mailbox that is running, plus
  // every one waiting on payment, since paying is what brings it back.
  const billable = boxes.filter(box => box.status === 'active' || box.suspended_for_billing)

  const domain: SetupStep =
    verified.length > 0
      ? {
          key: 'domain',
          title: 'Domain',
          state: 'done',
          summary: verified.length === 1 ? `${verified[0].name} is verified` : `${verified.length} domains verified`,
        }
      : live.length > 0
        ? {
            key: 'domain',
            title: 'Domain',
            state: 'current',
            summary: `Publish the DNS records for ${live[0].name}, then verify`,
          }
        : { key: 'domain', title: 'Domain', state: 'current', summary: 'Add the domain your email will use' }

  const price = monthlyPrice(pricing)
  const balance = etbBalance(wallet)
  let payment: SetupStep
  if (price == null || !wallet) {
    payment = { key: 'payment', title: 'Payment', state: 'todo', summary: 'Loading the price and your balance' }
  } else if (unpaid.length > 0) {
    payment = {
      key: 'payment',
      title: 'Payment',
      state: 'attention',
      summary: `${plural(unpaid.length, 'mailbox')} paused for payment. Top up to resume.`,
    }
  } else if (billable.length > 0) {
    const due = billable.reduce((total, box) => total + Number(box.monthly_etb ?? price), 0)
    payment =
      balance >= due
        ? {
            key: 'payment',
            title: 'Payment',
            state: 'done',
            summary: `${etb(balance)} covers ${plural(monthsCovered(balance, due), 'renewal', 'renewals')} of ${plural(billable.length, 'mailbox')}`,
          }
        : {
            key: 'payment',
            title: 'Payment',
            state: 'attention',
            summary: `Add ${etb(shortfall(balance, due))} to cover the next renewal`,
          }
  } else {
    payment =
      balance >= price
        ? { key: 'payment', title: 'Payment', state: 'done', summary: `${etb(balance)} in your wallet. The first month is ${etb(price)}.` }
        : { key: 'payment', title: 'Payment', state: 'attention', summary: `Add ${etb(shortfall(balance, price))} to cover the first month` }
  }

  const step3: SetupStep =
    boxes.length > 0
      ? { key: 'mailboxes', title: 'Mailboxes', state: 'done', summary: plural(boxes.length, 'mailbox') }
      : verified.length > 0
        ? { key: 'mailboxes', title: 'Mailboxes', state: 'current', summary: 'Create your first mailbox' }
        : { key: 'mailboxes', title: 'Mailboxes', state: 'todo', summary: 'Unlocks once a domain is verified' }

  return [domain, payment, step3]
}


